import { collection, getDocs, getFirestore, query, where } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { generarLibroDiario } from "./asientosService";

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());
const cleanNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const conciliarSireContabilidad = async ({ rucEmpresa, periodo }) => {
  const ruc = cleanText(rucEmpresa);
  const per = cleanText(periodo);

  if (!ruc || !per) {
    throw new Error("RUC y Periodo son obligatorios para la conciliación SIRE.");
  }

  const findings = [];
  let severeCount = 0;

  // 1. Fetch presentations from gco_sire_presentaciones
  const qPres = query(
    collection(db, CONT_COLLECTIONS.SIRE_PRESENTACIONES),
    where("ruc_empresa", "==", ruc),
    where("periodo", "==", per)
  );
  const snapPres = await getDocs(qPres);
  const presentaciones = snapPres.docs.map((d) => d.data());

  // 2. Fetch all asientos
  const asientos = await generarLibroDiario({ rucEmpresa: ruc, periodo: per });

  // Calculate totals from contabilidad (asientos)
  let totalDebe12 = 0;
  let totalHaber42 = 0;
  let totalIgvVentasAsientos = 0;
  let totalIgvComprasAsientos = 0;

  asientos.forEach((seat) => {
    if (!seat.cuo) return;

    // Check account 121 (Clients/Ventas) vs account 421 (Suppliers/Compras)
    (seat.debe || []).forEach((d) => {
      const c = cleanText(d.cuenta);
      if (c.startsWith("121")) totalDebe12 += cleanNumber(d.monto);
      if (c.startsWith("401111")) totalIgvVentasAsientos += cleanNumber(d.monto); // IGV Ventas
    });

    (seat.haber || []).forEach((h) => {
      const c = cleanText(h.cuenta);
      if (c.startsWith("421")) totalHaber42 += cleanNumber(h.monto);
      if (c.startsWith("401111")) totalIgvComprasAsientos += cleanNumber(h.monto); // IGV Compras
    });
  });

  // Calculate totals from SIRE presentations
  let totalVentasTributarias = 0;
  let totalComprasTributarias = 0;
  let igvVentasSire = 0;
  let igvComprasSire = 0;

  presentaciones.forEach((pres) => {
    const tipo = cleanText(pres.tipo).toUpperCase();
    if (tipo === "RVIE") {
      // simulated totals or extracted from presentation payload
      totalVentasTributarias += cleanNumber(pres.total || pres.monto || 0);
      igvVentasSire += cleanNumber(pres.igv || 0);
    } else if (tipo === "RCE") {
      totalComprasTributarias += cleanNumber(pres.total || pres.monto || 0);
      igvComprasSire += cleanNumber(pres.igv || 0);
    }
  });

  // Check differences in IGV
  const diffVentasIgv = Math.abs(totalIgvVentasAsientos - igvVentasSire);
  const diffComprasIgv = Math.abs(totalIgvComprasAsientos - igvComprasSire);

  if (diffVentasIgv > 0.05) {
    severeCount++;
    findings.push({
      id: "CONC_DIFF_IGV_VENTAS",
      severidad: "ALTA",
      mensaje: `Discrepancia en IGV Ventas: Contabilidad (${totalIgvVentasAsientos.toFixed(2)}) vs SIRE (${igvVentasSire.toFixed(2)}).`,
    });
  }

  if (diffComprasIgv > 0.05) {
    severeCount++;
    findings.push({
      id: "CONC_DIFF_IGV_COMPRAS",
      severidad: "ALTA",
      mensaje: `Discrepancia en IGV Compras: Contabilidad (${totalIgvComprasAsientos.toFixed(2)}) vs SIRE (${igvComprasSire.toFixed(2)}).`,
    });
  }

  // Check Notes of Credit without matching original sales documents
  // Fetching sales documents to verify
  const qVentas = query(
    collection(db, "os_comprobantes_venta"),
    where("ruc_empresa", "==", ruc),
    where("periodo", "==", per)
  );
  const snapVentas = await getDocs(qVentas);
  const ventas = snapVentas.docs.map((d) => ({ id: d.id, ...d.data() }));

  ventas.forEach((v) => {
    if (v.tipo_comprobante === "07") { // Nota de Crédito
      const docRel = cleanText(v.documento_relacionado || v.comprobante_relacionado);
      if (!docRel) {
        severeCount++;
        findings.push({
          id: "CONC_NC_WITHOUT_ORIGIN",
          severidad: "ALTA",
          mensaje: `Nota de Crédito ${v.id} no cuenta con un documento de referencia o de origen registrado.`,
        });
      }
    }
  });

  const status = severeCount > 0 ? "NO CONCILIADO" : "CONCILIADO";

  return {
    status,
    findings,
    totalVentasTributarias,
    totalComprasTributarias,
    igvVentas: igvVentasSire,
    igvCompras: igvComprasSire,
  };
};
